import { APIRequestError, CONFIG, NetworkRequestError, OfflineError } from "./api";

let adminToken = null;
let adminCsrfToken = null;

export function setAdminToken(token) {
  adminToken = token;
}

export function setAdminCsrfToken(token) {
  adminCsrfToken = token;
  writeStoredAdminCsrfToken(token);
}

function extractAdminApiErrorMessage(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = extractAdminApiErrorMessage(item);
      if (message) return message;
    }
    return "";
  }
  if (typeof value === "object") {
    return (
      extractAdminApiErrorMessage(value.detail) ||
      extractAdminApiErrorMessage(value.msg) ||
      extractAdminApiErrorMessage(value.message) ||
      extractAdminApiErrorMessage(value.error) ||
      extractAdminApiErrorMessage(value.title) ||
      ""
    );
  }
  return "";
}

function normalizeAdminUserId(userId) {
  const raw = String(userId ?? "").trim();
  if (!raw) {
    throw new Error("User ID is missing.");
  }
  if (!/^\d+$/.test(raw)) {
    throw new Error("User ID must be numeric.");
  }
  const parsed = Number(raw);
  if (!Number.isSafeInteger(parsed) || parsed < 1) {
    throw new Error("User ID is invalid.");
  }
  return String(parsed);
}

function normalizeAdminAccountId(userId) {
  const raw = String(userId ?? "").trim();
  if (!raw) {
    throw new Error("User ID is missing.");
  }
  if (!/^-?\d+$/.test(raw)) {
    throw new Error("User ID must be numeric.");
  }
  const parsed = Number(raw);
  if (!Number.isSafeInteger(parsed) || parsed === 0) {
    throw new Error("User ID is invalid.");
  }
  return String(parsed);
}

function clearAdminToken() {
  adminToken = null;
  clearStoredAdminCsrfToken();
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem("offlico.admin.token");
    } catch {
      // ignore storage failures
    }
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("admin:invalid"));
  }
}

export function writeStoredAdminCsrfToken(value) {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      if (value) {
        window.localStorage.setItem("offlico.admin.csrf", value);
      } else {
        window.localStorage.removeItem("offlico.admin.csrf");
      }
      return;
    } catch {
      // ignore storage failures
    }
  }
}

export function clearStoredAdminCsrfToken() {
  writeStoredAdminCsrfToken(null);
  adminCsrfToken = null;
}

function readStoredAdminToken() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      return window.localStorage.getItem("offlico.admin.token");
    } catch {
      return null;
    }
  }
  return null;
}

function readStoredAdminCsrfToken() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      return window.localStorage.getItem("offlico.admin.csrf");
    } catch {
      return null;
    }
  }
  return null;
}

function getAdminCsrfToken() {
  if (adminCsrfToken !== null && adminCsrfToken !== undefined) {
    return adminCsrfToken;
  }
  return readStoredAdminCsrfToken();
}

function getAdminToken() {
  return adminToken || readStoredAdminToken();
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function reloadAdminIfStaleBuild() {
  if (typeof window === "undefined") return false;
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return false;
    const data = await res.json();
    const version = data?.git_sha || data?.build_id || data?.version;
    const currentBuild = window.__OFFLICO_BUILD_ID__ || "";
    if (!version || !currentBuild || String(version).startsWith(currentBuild)) return false;
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

async function adminFetch(path, options = {}) {
  const method = String(options.method || "GET").toUpperCase();
  const isReadOnly = method === "GET" || method === "HEAD" || method === "OPTIONS";
  const isLoginRequest = method === "POST" && path === "/api/admin/auth/login";
  const maxAttempts = isReadOnly ? 2 : isLoginRequest ? 3 : 1;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const token = getAdminToken();
      const csrfToken = getAdminCsrfToken();
      const isAdminScoped = path.startsWith("/api/admin") || path.startsWith("/admin");
      const isFormData =
        typeof FormData !== "undefined" &&
        options?.body instanceof FormData;
      const res = await fetch(`${CONFIG.BASE}${path}`, {
        headers: {
          ...(!isFormData && !isReadOnly ? { "Content-Type": "application/json" } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(isAdminScoped && !isReadOnly && csrfToken
            ? { "X-Admin-CSRF-Token": csrfToken }
            : {}),
          ...(options.headers || {}),
        },
        ...options,
      });

      if (!res.ok) {
        if (res.status === 401) {
          clearAdminToken();
        }
        const text = await res.text().catch(() => "");
        const rawMessage = extractAdminApiErrorMessage(
          (() => {
            const trimmed = String(text || "").trim();
            if (!trimmed) return "";
            try {
              return JSON.parse(trimmed);
            } catch {
              return trimmed;
            }
          })()
        );
        throw new APIRequestError({
          status: res.status,
          statusText: res.statusText,
          message: rawMessage || `${res.status} ${res.statusText}`,
          rawMessage,
          responseBody: text,
        });
      }

      if (res.status === 204) return null;
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const text = await res.text().catch(() => "");
        if (!text) return null;
        return JSON.parse(text);
      }
      return null;
    } catch (err) {
      const isNetworkError =
        err?.name === "TypeError" || err?.message === "Failed to fetch";
      if (isNetworkError && typeof navigator !== "undefined" && navigator.onLine === false) {
        throw new OfflineError();
      }
      if (isNetworkError && attempt < maxAttempts) {
        await wait(350);
        continue;
      }
      if (isNetworkError) {
        if (await reloadAdminIfStaleBuild()) {
          return null;
        }
        throw new NetworkRequestError("Unable to reach the admin API. Please refresh and try again.");
      }
      throw err;
    }
  }
  throw new NetworkRequestError("Unable to reach the admin API. Please refresh and try again.");
}

async function adminFetchBlob(path) {
  const token = getAdminToken();
  const res = await fetch(`${CONFIG.BASE}${path}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${text}`.trim());
  }
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") || "";
  return { blob, disposition };
}

export function adminLogin(payload) {
  return adminFetch(`/api/admin/auth/login`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getAdminKpis() {
  return adminFetch(`/api/admin/kpis`);
}

export function getAdminOpsSummary() {
  return adminFetch(`/api/admin/ops-summary`);
}

export function getAdminAiReceptionist() {
  return adminFetch(`/api/admin/ai-receptionist`);
}

export function updateAdminAiReceptionistIntegrationRequest(requestId, payload) {
  return adminFetch(`/api/admin/ai-receptionist/integration-requests/${requestId}`, {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
  });
}

export function getAdminUsers({ limit = 50, offset = 0, q = "" } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  const trimmed = String(q || "").trim();
  if (trimmed) params.set("q", trimmed);
  return adminFetch(`/api/admin/users?${params.toString()}`);
}

export function getAdminUsersOverview({ activeRangeDays = 1, journeyDate = "" } = {}) {
  const params = new URLSearchParams({ active_range_days: String(activeRangeDays) });
  const normalizedJourneyDate = String(journeyDate || "").trim();
  if (normalizedJourneyDate) params.set("journey_date", normalizedJourneyDate);
  return adminFetch(`/api/admin/users/overview?${params.toString()}`);
}

export function getAdminEmails({ limit = 200, offset = 0, q = "", source = "" } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  const trimmedQuery = String(q || "").trim();
  const trimmedSource = String(source || "").trim();
  if (trimmedQuery) params.set("q", trimmedQuery);
  if (trimmedSource) params.set("source", trimmedSource);
  return adminFetch(`/api/admin/emails?${params.toString()}`);
}

export function getAdminCustomerDemand({ limit = 100, offset = 0, q = "", source = "", status = "", profession = "" } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (q) params.set("q", q);
  if (source) params.set("source", source);
  if (status) params.set("status_filter", status);
  if (profession) params.set("profession", profession);
  return adminFetch(`/api/admin/customer-demand?${params.toString()}`);
}

export function getAdminCustomerHub({ limit = 100, offset = 0, q = "", area = "", profession = "" } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (q) params.set("q", q);
  if (area) params.set("area", area);
  if (profession) params.set("profession", profession);
  return adminFetch(`/api/admin/customer-hub?${params.toString()}`);
}

export function updateAdminCustomerDemandLead(leadId, payload = {}) {
  return adminFetch(`/api/admin/customer-demand/${encodeURIComponent(leadId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
  });
}

export function deleteAllCustomerDemandRequests(confirm = "DELETE ALL CUSTOMER DEMAND REQUESTS") {
  const params = new URLSearchParams({ confirm });
  return adminFetch(`/api/admin/customer-demand?${params.toString()}`, { method: "DELETE" });
}

export function getAdminClientDatabase({ limit = 100, offset = 0, q = "" } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (q) params.set("q", q);
  return adminFetch(`/api/admin/client-database?${params.toString()}`);
}

export function getAdminUserDetail(
  userId,
  { rangeDays = 30, includeMessages = false, messageLimit = 25, logLimit = 50 } = {}
) {
  const resolvedUserId = normalizeAdminAccountId(userId);
  const params = new URLSearchParams({
    range_days: String(rangeDays),
    include_messages: includeMessages ? "1" : "0",
    message_limit: String(messageLimit),
    log_limit: String(logLimit),
  });
  return adminFetch(`/api/admin/users/${resolvedUserId}/detail?${params.toString()}`);
}

export function getAdminSubscriptions() {
  return adminFetch(`/api/admin/subscriptions`);
}

export function getAdminFeatureUsage({ rangeDays = 30 } = {}) {
  return adminFetch(`/api/admin/feature-usage?range_days=${rangeDays}`);
}

export function getAdminMessagingAnalytics({ rangeDays = 30 } = {}) {
  return adminFetch(`/api/admin/analytics/messaging?range_days=${rangeDays}`);
}

export function getAdminGrowthAnalytics({ rangeDays = 30 } = {}) {
  return adminFetch(`/api/admin/analytics/growth?range_days=${rangeDays}`);
}

export function getAdminFeatureAdoption({ rangeDays = 30 } = {}) {
  return adminFetch(`/api/admin/analytics/feature-adoption?range_days=${rangeDays}`);
}

export function getAdminUserAnalytics({ rangeDays = 30 } = {}) {
  return adminFetch(`/api/admin/analytics/users?range_days=${rangeDays}`);
}

export function getAdminIntelligence({ rangeDays = 30 } = {}) {
  return adminFetch(`/api/admin/analytics/intelligence?range_days=${rangeDays}`);
}

export function getAdminAwareness({ rangeDays = 30 } = {}) {
  return adminFetch(`/api/admin/awareness?range_days=${rangeDays}`);
}

export function getAdminAuditLogs({ limit = 50, offset = 0 } = {}) {
  return adminFetch(`/api/admin/audit?limit=${limit}&offset=${offset}`);
}

export function getAdminReminderCredits({ limit = 200, ledgerLimit = 200 } = {}) {
  return adminFetch(`/api/admin/reminder-credits?limit=${limit}&ledger_limit=${ledgerLimit}`);
}

// LocalPickr gift cards (docs/localpickr/GIFT-CARDS.md).
export function getAdminGiftCards() {
  return adminFetch(`/api/admin/gift-cards`);
}

export function refundAdminGiftCard(cardId, { force = false } = {}) {
  return adminFetch(`/api/admin/gift-cards/${cardId}/refund`, {
    method: "POST",
    body: JSON.stringify({ force: Boolean(force) }),
  });
}

export function getAdminUsageSummary({ rangeDays = 7, period = "" } = {}) {
  const params = new URLSearchParams({ range_days: String(rangeDays) });
  if (period) {
    params.set("period", period);
  }
  return adminFetch(`/api/admin/usage/summary?${params.toString()}`);
}

export function getAdminOperationalHealth({ rangeDays = 30 } = {}) {
  return adminFetch(`/api/admin/health?range_days=${rangeDays}`);
}

export function getAdminUsageUsers({
  rangeDays = 7,
  limit = 25,
  offset = 0,
  query = "",
  sortBy = "",
  sortDir = "desc",
} = {}) {
  const params = new URLSearchParams({
    range_days: String(rangeDays),
    limit: String(limit),
    offset: String(offset),
  });
  if (query) params.set("q", query);
  if (sortBy) params.set("sort_by", sortBy);
  if (sortDir) params.set("sort_dir", sortDir);
  return adminFetch(`/api/admin/usage/users?${params.toString()}`);
}

export function getAdminUsageUserDetail(userId, { rangeDays = 7 } = {}) {
  const resolvedUserId = normalizeAdminUserId(userId);
  return adminFetch(`/api/admin/usage/users/${resolvedUserId}?range_days=${rangeDays}`);
}

export function revealAdminUser(userId) {
  const resolvedUserId = normalizeAdminAccountId(userId);
  return adminFetch(`/api/admin/users/${resolvedUserId}/reveal`);
}

export function exportAdminUser(userId) {
  const resolvedUserId = normalizeAdminAccountId(userId);
  return adminFetch(`/api/admin/users/${resolvedUserId}/export`);
}

export function deactivateAdminUser(userId) {
  const resolvedUserId = normalizeAdminUserId(userId);
  return adminFetch(`/api/admin/users/${resolvedUserId}/deactivate`, { method: "POST" });
}

export function reactivateAdminUser(userId) {
  const resolvedUserId = normalizeAdminUserId(userId);
  return adminFetch(`/api/admin/users/${resolvedUserId}/reactivate`, { method: "POST" });
}

export function deleteAdminUser(userId, { confirmEmail } = {}) {
  const resolvedUserId = normalizeAdminAccountId(userId);
  const params = new URLSearchParams();
  if (confirmEmail) params.set("confirm_email", confirmEmail);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return adminFetch(`/api/admin/users/${resolvedUserId}${suffix}`, { method: "DELETE" });
}

export function restoreAdminUserBackup(backupPath) {
  const params = new URLSearchParams({ backup_path: String(backupPath || "") });
  return adminFetch(`/api/admin/users/restore?${params.toString()}`, { method: "POST" });
}

export function getAdminUserNotes(userId, { limit = 50, offset = 0 } = {}) {
  const resolvedUserId = normalizeAdminUserId(userId);
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return adminFetch(`/api/admin/users/${resolvedUserId}/notes?${params.toString()}`);
}

export function createAdminUserNote(userId, payload) {
  const resolvedUserId = normalizeAdminUserId(userId);
  const notePayload = typeof payload === "string" ? { note: payload } : payload || {};
  return adminFetch(`/api/admin/users/${resolvedUserId}/notes`, {
    method: "POST",
    body: JSON.stringify(notePayload),
  });
}

export function startAdminImpersonationSession({
  userId,
  reason = "Support review",
  readOnly = true,
} = {}) {
  const resolvedUserId = normalizeAdminUserId(userId);
  return adminFetch(`/api/admin/impersonations`, {
    method: "POST",
    body: JSON.stringify({
      target_user_id: resolvedUserId,
      reason,
      read_only: readOnly,
    }),
  });
}

export function getAdminImpersonationSessions({ includeEnded = false } = {}) {
  const params = new URLSearchParams();
  if (includeEnded) params.set("include_ended", "1");
  const query = params.toString();
  return adminFetch(`/api/admin/impersonations${query ? `?${query}` : ""}`);
}

export function endAdminImpersonationSession(sessionId) {
  return adminFetch(`/api/admin/impersonations/${sessionId}`, {
    method: "DELETE",
  });
}

export function updateAdminUserPlan(userId, payload) {
  const resolvedUserId = normalizeAdminUserId(userId);
  return adminFetch(`/api/admin/users/${resolvedUserId}/plan`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function requestAdminPlanVerification(userId) {
  const resolvedUserId = normalizeAdminUserId(userId);
  return adminFetch(`/api/admin/users/${resolvedUserId}/plan/verify`, {
    method: "POST",
  });
}

export function getAdminGeocodeMissing() {
  return adminFetch(`/api/admin/geocode-missing`);
}

export function runAdminGeocodeMissing() {
  return adminFetch(`/api/admin/geocode-missing`, {
    method: "POST",
  });
}

export function getAdminBlogPosts({ status = "", limit = 100, q = "" } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
  });
  if (status) params.set("status", status);
  if (q) params.set("q", q);
  return adminFetch(`/api/admin/blog/posts?${params.toString()}`);
}

export function getAdminBlogPost(postId) {
  return adminFetch(`/api/admin/blog/posts/${postId}`);
}

export function createAdminBlogPost(payload) {
  return adminFetch(`/api/admin/blog/posts`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function generateAdminBlogPost(payload) {
  return adminFetch(`/api/admin/blog/posts/generate`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function updateAdminBlogPost(postId, payload) {
  return adminFetch(`/api/admin/blog/posts/${postId}`, {
    method: "PUT",
    body: JSON.stringify(payload || {}),
  });
}

export function publishAdminBlogPost(postId) {
  return adminFetch(`/api/admin/blog/posts/${postId}/publish`, {
    method: "POST",
  });
}

export function unpublishAdminBlogPost(postId) {
  return adminFetch(`/api/admin/blog/posts/${postId}/unpublish`, {
    method: "POST",
  });
}

export function uploadAdminBlogImage(file) {
  const formData = new FormData();
  formData.append("file", file);
  return adminFetch(`/api/admin/blog/images`, {
    method: "POST",
    body: formData,
    headers: {},
  });
}

export function getAdminMigrationRequests({ status = "", limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (status) params.set("status", status);
  return adminFetch(`/api/admin/migrations/requests?${params.toString()}`);
}

export function getAdminBusinessVerificationRequests({ status = "", limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (status) params.set("status", status);
  return adminFetch(`/api/admin/business-verifications?${params.toString()}`);
}

export function updateAdminBusinessVerificationRequest(requestId, payload) {
  return adminFetch(`/api/admin/business-verifications/${requestId}`, {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
  });
}

export function getAdminBugReports({ status = "", source = "", limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (status) params.set("status", status);
  if (source) params.set("source", source);
  return adminFetch(`/api/admin/bug-reports?${params.toString()}`);
}

export function getAdminBugReport(reportId) {
  return adminFetch(`/api/admin/bug-reports/${reportId}`);
}

export function updateAdminBugReport(reportId, payload) {
  return adminFetch(`/api/admin/bug-reports/${reportId}`, {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
  });
}

export function downloadAdminBugReportAttachment(reportId, attachmentId) {
  return adminFetchBlob(`/api/admin/bug-reports/${reportId}/attachments/${attachmentId}`);
}

export function getAdminMigrationRequest(requestId) {
  return adminFetch(`/api/admin/migrations/requests/${requestId}`);
}

export function getAdminMigrationWorkflow(requestId) {
  return adminFetch(`/api/admin/migrations/requests/${requestId}/workflow`);
}

export function parseAdminMigrationFile(requestId, fileId) {
  const query = fileId ? `?file_id=${fileId}` : "";
  return adminFetch(`/api/admin/migrations/requests/${requestId}/workflow/parse${query}`, {
    method: "POST",
  });
}

export function saveAdminMigrationMapping(requestId, payload) {
  return adminFetch(`/api/admin/migrations/requests/${requestId}/workflow/mapping`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function validateAdminMigration(requestId) {
  return adminFetch(`/api/admin/migrations/requests/${requestId}/workflow/validate`, {
    method: "POST",
  });
}

export function importAdminMigration(requestId, fileId) {
  const query = fileId ? `?file_id=${fileId}` : "";
  return adminFetch(`/api/admin/migrations/requests/${requestId}/workflow/import${query}`, {
    method: "POST",
  });
}

export function completeAdminMigration(requestId) {
  return adminFetch(`/api/admin/migrations/requests/${requestId}/workflow/complete`, {
    method: "POST",
  });
}

export function aiMapAdminMigration(requestId, fileId) {
  const query = fileId ? `?file_id=${fileId}` : "";
  return adminFetch(`/api/admin/migrations/requests/${requestId}/workflow/ai-map${query}`, {
    method: "POST",
  });
}

export function updateAdminMigrationRequest(requestId, payload) {
  return adminFetch(`/api/admin/migrations/requests/${requestId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function replaceAdminMigrationFile(requestId, files) {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  return adminFetch(`/api/admin/migrations/requests/${requestId}/file`, {
    method: "POST",
    body: formData,
    headers: {},
  });
}

export function downloadAdminMigrationFile(requestId, fileId) {
  const path = fileId
    ? `/api/admin/migrations/requests/${requestId}/files/${fileId}`
    : `/api/admin/migrations/requests/${requestId}/file`;
  return adminFetchBlob(path);
}
